import IVStepper from 'components/common/IVStepper';
import Page from 'components/Page';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {cn, DEVICE, useScreenResize} from '@chekin/guestapp-ui-kit';
import {Outlet, useLocation, useMatch, useNavigate} from 'react-router-dom';
import {usePath} from 'hooks';
import {PATHS} from 'routes/constants';
import {useOpenModals} from 'context/openModals';
import {useLayoutMode} from 'hooks/useLayoutMode';
import ChekinProSettings from '../../ChekinProSettings';
import DiscardLastPhotoModal from 'components/common/DiscardLastPhotoModal';
import {DiscardLastPhotoModalRef} from 'components/common/DiscardLastPhotoModal/DiscardLastPhotoModal';
import {TreatImagesInfoModal, IdentityContainer} from './styled';
import {VerificationCompletedProvider} from 'components/VerificationCompletedModal';
import {useIVOnProperty} from 'hooks/useIVOnProperty';
import {IVUnavailableView} from './views/IVUnavailableView';
import {useIVRemoteSync} from 'hooks/iv/useIVRemoteSync';
import {SDK_MODE} from '@chekin/core';

function IdentityVerification() {
  const {isLoaded} = useIVRemoteSync();
  const {t} = useTranslation('identity-verification');
  const {isMatch: isMobileMode} = useScreenResize(DEVICE.laptop);
  const discardLastPhotoModalRef = React.useRef<DiscardLastPhotoModalRef>(null);
  const {checkIfOpenDiscardModalAndRedirect} = useOpenModals();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const layoutMode = useLayoutMode();

  const isRootVerification = useMatch('/:token?/verification/:id?');
  const isStartView = useMatch('/:token?/verification');
  const isScanQR = useMatch('/:token?/verification/:id?/scan-qr-code');
  const isSelfieInstructionsView = useMatch(
    '/:token?/verification/:id?/selfie-instructions',
  );
  const isContactsVerificationView = useMatch('/:token?/verification/contacts');
  const isVerificationCompleted = useMatch(
    '/:token?/verification/verification-completed',
  );
  const isSelfieView = useMatch('/:token?/verification/selfie');
  const areSelfieViews = isSelfieView || isSelfieInstructionsView;

  const isQRScanGuestView = useMatch('/:token?/verification/:id?/qr-scan');
  const isQRScannerGuestView = useMatch('/:token?/verification/:id?/qr-scanner');
  const isScanningProcess = Boolean(isQRScanGuestView || isQRScannerGuestView);

  const shouldHideStepper =
    isContactsVerificationView ||
    (isRootVerification && !areSelfieViews) ||
    isScanQR ||
    isScanningProcess ||
    isVerificationCompleted;

  const isFrontSideView = useMatch('/:token?/verification/front/take');
  const isBackSideView = useMatch('/:token?/verification/back/take');
  const isBackSideUploadTypeSelection = useMatch(
    '/:token?/verification/uploading-type/back',
  );
  const isBackOrSelfieView =
    pathname.includes(PATHS.verification.selfie) || isBackSideUploadTypeSelection;

  const openDiscardIVLastPhotoModal = () =>
    discardLastPhotoModalRef.current?.open(PATHS.verification.main);

  const onBackButtonClick = () =>
    isBackOrSelfieView
      ? openDiscardIVLastPhotoModal()
      : isScanningProcess
      ? navigate(getFullPath(PATHS.guestRegistration), {replace: true})
      : isRootVerification
      ? navigate(getFullPath(PATHS.home), {replace: true})
      : checkIfOpenDiscardModalAndRedirect('-1');

  const isTakeView =
    isMobileMode &&
    (Boolean(isFrontSideView) ||
      Boolean(isBackSideView) ||
      Boolean(isSelfieView) ||
      Boolean(isQRScannerGuestView));

  const isSubHeaderVisible =
    ChekinProSettings.mode === SDK_MODE.onlyIV
      ? !isStartView && !isTakeView && !isScanningProcess
      : !isTakeView;

  const navbarTitle = isScanningProcess
    ? t('scan_qr_code')
    : isMobileMode || layoutMode === 'kiosko'
    ? t('identity_verification')
    : t('guest_registration');

  const {isIVOnPropertyUnavailable} = useIVOnProperty();

  if (isIVOnPropertyUnavailable) {
    return (
      <IdentityContainer>
        <Page
          isBackButtonDisabled={false}
          onBackButtonClick={onBackButtonClick}
          navBarTitle={t('scan_qr_code')}
          title={t('scan_qr_code')}
          mainHeader={!isMobileMode}
          isSubHeaderVisible={isMobileMode}
        >
          <IVUnavailableView />
        </Page>
      </IdentityContainer>
    );
  }

  return (
    <VerificationCompletedProvider>
      <IdentityContainer>
        <Page
          isSubHeaderVisible={layoutMode === 'default' && isSubHeaderVisible}
          isBackButtonDisabled={
            isMobileMode ||
            Boolean(ChekinProSettings.mode === SDK_MODE.onlyIV && isStartView)
          }
          isLoading={!isLoaded}
          title={isScanningProcess ? '' : t('identity_verification')}
          onBackButtonClick={onBackButtonClick}
          navBarTitle={navbarTitle}
          className={cn('iv-flow-layout', !isStartView && 'kiosko:pt-[108px]')}
          mainHeader={ChekinProSettings.mode === SDK_MODE.onlyIV ? false : !isMobileMode}
        >
          {layoutMode === 'default' && <TreatImagesInfoModal />}
          {(!shouldHideStepper || (layoutMode === 'kiosko' && !isStartView)) && (
            <IVStepper onClickBackMobile={onBackButtonClick} />
          )}
          <DiscardLastPhotoModal ref={discardLastPhotoModalRef} />
          {isLoaded && <Outlet />}
        </Page>
      </IdentityContainer>
    </VerificationCompletedProvider>
  );
}

export {IdentityVerification};
